import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ApiBulkSession, ApiBulkUploadResultState, ApiBulkUploadStatus } from '@agunity/api-v4';
import { Box, Divider, Grid, Stack } from '@mui/material';

import AgAlert from '../../../components/AgAlert/Alert';
import AgTypography from '../../../stories/Styles/AgTypography';
import AgButton from '../../../stories/Components/AgButton';
import AgLoadingBackdrop from '../../../components/AgLoadingBackdrop';
import { AgColor } from '../../../stories/Styles/AgColor';

import { formatDateTime } from '../../../utils';

const LabelValue = ({ label, value }: { label: string, value: string }) => {
  return (
    <Stack direction='row' gap={5}>
      <AgTypography
        contentStyle='AP.H5.UPPERCASE.Bold'
        message={label}
        sx={{ minWidth: 100 }}
      />
      <Stack direction='row' gap={1}>
        <AgTypography
          contentStyle='AP.H5.Medium'
          message={':'}
        />
        <AgTypography
          contentStyle='AP.H5.Medium'
          message={value}
          style={{ color: AgColor.grey6 }}
        />
      </Stack>
    </Stack>
  );
};

const Header = ({ details, onSubmit }: { details: ApiBulkSession, onSubmit?: () => void }) => {
  const { formatMessage } = useIntl();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUploadedState = details.state === ApiBulkUploadStatus.Uploaded;
  const isFailedToValidateState = details.resultState === ApiBulkUploadResultState.FailedValidate;
  const hasErrors = (details?.errors?.length || 0) > 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit?.();
    setIsSubmitting(false);
  };

  return (
    <>
      {isSubmitting ? <AgLoadingBackdrop /> : null}
      <Grid item xs={12} width='100%'>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <AgTypography
            contentStyle='AP.H5.UPPERCASE.Bold'
            message={`${formatMessage({ id: 'ID' })} #${details.id}`}
            style={{ color: AgColor.grey6 }}
          />

          {isUploadedState && (
            <AgButton
              label={formatMessage({ id: 'BULK_UPLOAD_REQUEST_SUBMIT' })}
              style={{ position: 'absolute', right: 0, top: 0 }}
              onClick={handleSubmit}
              disabled={hasErrors || isFailedToValidateState}
              loading={isSubmitting}
            />
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <AgTypography
          contentStyle='AP.Paragraph.Small.Regular'
          message={formatMessage({ id: 'BULK_UPLOAD_REQUEST_DETAILS_DESC' })}
          style={{ color: AgColor.grey7 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <LabelValue
          label={formatMessage({ id: 'BULK_UPLOAD_REQUEST_SOURCE' })}
          value={formatMessage({ id: `BULK_UPLOAD_REQUEST_SOURCE_${details.source}` })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <LabelValue
          label={formatMessage({ id: 'BULK_UPLOAD_REQUEST_DATETIME_REQUESTED' })}
          value={formatDateTime(details.createdAsOf)}
        />
      </Grid>

      <Grid item xs={12}>
        <LabelValue
          label={formatMessage({ id: 'BULK_UPLOAD_REQUEST_REQUESTER' })}
          value={details.userEmail || ''}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <LabelValue
          label={formatMessage({ id: 'BULK_UPLOAD_REQUEST_STATUS' })}
          value={formatMessage({ id: `BULK_UPLOAD_REQUEST_STATE_${details.state}` })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <LabelValue
          label={formatMessage({ id: 'BULK_UPLOAD_REQUEST_RESULT' })}
          value={formatMessage({ id: `BULK_UPLOAD_REQUEST_RESULT_STATE_${details.resultState}` })}
        />
      </Grid>

      {hasErrors && <AgAlert type='customError' message={formatMessage({ id: 'BULK_UPLOAD_REQUEST_ADVICE_DESC' })} />}

      <Grid item xs={12}>
        <Divider />
      </Grid>
    </>
  );
};

export default Header;